/**
 * Calculate and format inverter inverter related values
 */
import { i18n } from '@/helpers/i18n';
import {
  formatInverterTotalActivePower,
  formatInverterTotalApparentPower,
  formatInverterTotalNominalPower,
  inverterTotalActivePower,
  inverterTotalNominalPower,
} from '@/helpers/solarPlant';

const getProjectInverters = (project) => {
  return Array.isArray(project?.solarPlant?.inverters) ? project.solarPlant.inverters : [];
};

const uniqueInverterValues = (project, getter) => {
  return [
    ...new Set(
      getProjectInverters(project)
        .map((inverter) => getter(inverter))
        .filter(Boolean)
    ),
  ];
};

export const inverterCount = (project) => {
  return getProjectInverters(project).reduce(
    (count, inverter) => count + parseInt(inverter.count || 0, 10),
    0
  );
};

/**
 * Determine how many identical inverters are assigned to a project
 * @param {*} project
 * @returns
 */
export const inverterIdenticalCount = (project) => {
  const inverters = getProjectInverters(project);
  if (inverters.length === 0) {
    return null;
  }

  const uniqueItems = new Set(
    inverters.map((inverter) => inverter.item?._id || inverter.item).filter(Boolean)
  );

  if (uniqueItems.size === 1) {
    return inverterCount(project);
  }

  return 1;
};

/**
 * Scheinleistung Summe Inverter 1 + 2, formatiert
 * @param {*} project
 * @returns
 */
export const formatInverterApparentPowerOverall = (project) => {
  return formatInverterTotalApparentPower(project?.solarPlant?.inverters);
};

/**
 * Wirkleistung Summe Inverter 1 + 2, formatiert
 * @param {*} project
 * @returns
 */
export const formatInverterActivePowerOverall = (project) => {
  return formatInverterTotalActivePower(project?.solarPlant?.inverters);
};

/**
 * Nennleistung Summe Inverter 1 + 2, formatiert
 * @param {*} project
 * @returns
 */
export const inverterNominalPowerOverall = (project) => {
  return inverterTotalNominalPower(project?.solarPlant?.inverters);
};

/**
 * Nennleistung Summe, formatiert
 * @param {*} project
 * @returns
 */
export const formatInverterNominalPowerOverall = (project) => {
  return formatInverterTotalNominalPower(project?.solarPlant?.inverters);
};

/**
 * Wirkleistung Summe
 * @param {*} project
 * @returns
 */
export const inverterActivePowerOverall = (project) => {
  return inverterTotalActivePower(project?.solarPlant?.inverters);
};

/**
 * nominalPower / number of selected phases in netOutputState
 * @param {*} project
 * @returns
 */
export const formatInverterNominalPowerPerPhase = (project) => {
  const phases = phaseCount(project.solarPlant.netOutputStates);
  const power = inverterNominalPowerOverall(project);
  // console.log(power, phases, power / phases);
  return i18n.n(power / phases, 'decimal_triple');
};

/**
 * activePower / number of selected phases in netOutputState
 * @param {*} project
 * @returns
 */
export const formatInverterActivePowerPerPhase = (project) => {
  const phases = phaseCount(project.solarPlant.netOutputStates);
  const power = inverterActivePowerOverall(project);
  return i18n.n(power / phases, 'decimal_triple');
};

export const formatInverterActivePowerByPhase = (project, phase) => {
  const nrPhases = phaseCount(project.solarPlant.netOutputStates);
  const power = inverterActivePowerOverall(project);

  if (project.solarPlant.netOutputStates.find((state) => state === 'THREEWAY_CURRENT')) {
    return i18n.n(power / nrPhases, 'decimal_triple');
  }

  if (project.solarPlant.netOutputStates.find((state) => state === phase)) {
    return i18n.n(power / nrPhases, 'decimal_triple');
  }

  return i18n.n(0, 'decimal_triple');
};
export const formatInverterNominalPowerByPhase = (project, phase) => {
  const nrPhases = phaseCount(project.solarPlant.netOutputStates);
  const power = inverterNominalPowerOverall(project);

  if (project.solarPlant.netOutputStates.find((state) => state === 'THREEWAY_CURRENT')) {
    return i18n.n(power / nrPhases, 'decimal_triple');
  }

  if (project.solarPlant.netOutputStates.find((state) => state === phase)) {
    return i18n.n(power / nrPhases, 'decimal_triple');
  }

  return i18n.n(0, 'decimal_triple');
};

export const phaseCount = (netOutputStates) => {
  if (
    netOutputStates.length === 1 &&
    (netOutputStates[0] === 'L1' || netOutputStates[0] === 'L2' || netOutputStates[0] === 'L3')
  ) {
    return 1;
  }

  if (netOutputStates.length === 1 && netOutputStates[0] === 'THREEWAY_CURRENT') {
    return 3;
  }
  if (netOutputStates.length === 1) {
    return 1;
  }
  if (netOutputStates.length === 2) {
    return 2;
  }
  if (netOutputStates.length === 3) {
    return 3;
  }

  return 1;
};

export const formatInverterCompositeType = (project) => {
  return uniqueInverterValues(project, (inverter) => inverter.item?.inverter?.type).join(' / ');
};

export const formatInverterCompositeManufacturer = (project) => {
  return uniqueInverterValues(project, (inverter) => inverter.item?.inverter?.manufacturer).join(
    ' / '
  );
};
export const formatInverterCompositeManufacturerType = (project) => {
  return (
    formatInverterCompositeManufacturer(project) + ' - ' + formatInverterCompositeType(project)
  );
};
